import slugify from 'slugify';
import {
  DocumentSectionRevision,
  QualioDocument,
  QualioDocumentSection,
} from '../api/model/document';
import { ChangeControlSection } from '../types/DocumentCreate';
import { parseHTMLString } from './HTMLElementUtils';
import { isQriUrl } from './QriUrlUtils';
import { StringEscapeUtils } from './StringEscapeUtils';

import DOMPurify from 'dompurify';
import { documentApi } from '../api/document';
import { ChangeRequestSectionType } from '../views/components/ChangeRequestSideDrawer/types/ChangeRequest';

interface SectionMetadata {
  title: string;
  position: number;
}

const MAIL_TO_REGEX = /<mailto:(.+?)>[\s?]/;
const TEL_REGEX = /<tel:\+?(\d+)>/g;
const EMAIL_REGEX =
  /[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9-]{1,63}(?:\.[a-zA-Z0-9-]{1,63})*\.[a-zA-Z]{2,24}/g;

const domPurifyConfig = {
  KEEP_CONTENT: true,
  ADD_ATTR: [
    'multilevel',
    'multilevelFrame1',
    'multilevelFrame2',
    'multilevelFrame3',
    'multilevelFrame4',
    'multilevelFrame5',
  ],
};

const buildDOMPurifyConfig = () => {
  const newConfig: { [key: string]: any } = { ...domPurifyConfig };
  newConfig['ADD_TAGS'] = ['iframe'];
  newConfig['ADD_ATTR'] = [
    ...newConfig['ADD_ATTR'],
    'allow',
    'allowfullscreen',
    'frameborder',
    'scrolling',
  ];

  return newConfig;
};

const styliseTOC = (tableOfContents: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(tableOfContents, 'text/html');
  const tocElement = doc.querySelector('.table-of-contents');

  if (tocElement) {
    tocElement.classList.add('ck-widget');

    const wrapper = doc.createElement('div');
    wrapper.className = 'ck-content ck-editor__editable';

    while (doc.body.firstChild) {
      wrapper.appendChild(doc.body.firstChild);
    }
    return wrapper.outerHTML;
  }
  return tableOfContents;
};

interface SectionContentOptions {
  includeCollabData: boolean;
  forExport: boolean;
  useLegacyExport: boolean;
  sanitizeHtmlWithDomPurify: boolean;
  showTitle: boolean;
}

export class DocumentPresentationUtils {
  static async determineHtmlDocumentContentFromDocument(
    editorDocument: QualioDocument,
    includeCollabData = true,
    forExport = false,
    useLegacyExport = false,
    sanitizeHtmlWithDomPurify = false,
  ) {
    const purifyConfig = buildDOMPurifyConfig();
    let totalContent = '';
    if (editorDocument.table_of_contents) {
      totalContent += styliseTOC(editorDocument.table_of_contents);
    }
    const showTitle = editorDocument.text_sections_titles_visible;
    for (const section of editorDocument.sections) {
      totalContent +=
        DocumentPresentationUtils.determineSectionContentForDocument(
          section,
          {
            includeCollabData,
            forExport,
            useLegacyExport,
            sanitizeHtmlWithDomPurify,
            showTitle,
          },
          purifyConfig,
        );
    }
    if (forExport) {
      totalContent =
        await DocumentPresentationUtils.convertEmbedsToContent(totalContent);
    }
    return totalContent;
  }

  static stripMailtoTelAndEmailContent(content: string): string {
    let result = content;
    if (MAIL_TO_REGEX.test(result)) {
      result = DocumentPresentationUtils.stripMailtoContent(result);
    }
    if (TEL_REGEX.test(result)) {
      result = DocumentPresentationUtils.stripTelContent(result);
    }
    if (EMAIL_REGEX.test(result)) {
      result = DocumentPresentationUtils.stripEmailContent(result);
    }
    return result;
  }

  static determineSectionContentForDocument(
    section: QualioDocumentSection,
    options: SectionContentOptions,
    purifyConfig: { [key: string]: any },
  ): string {
    const {
      includeCollabData,
      forExport,
      useLegacyExport,
      sanitizeHtmlWithDomPurify,
      showTitle,
    } = options;
    const sectionTitle = DocumentPresentationUtils.determineSectionTitleTag(
      section,
      showTitle,
    );

    let content = section.content;
    if (!includeCollabData) {
      content = DocumentPresentationUtils.stripCollabData(content);
    }

    if (forExport) {
      content = DocumentPresentationUtils.convertEmbedsToLinks(content, false);
    }

    if (!sanitizeHtmlWithDomPurify) {
      content =
        DocumentPresentationUtils.stripMailtoTelAndEmailContent(content);
    }
    content = DocumentPresentationUtils.addFigureToTables(content);

    if (!showTitle && forExport) {
      content = useLegacyExport ? content : `${sectionTitle}${content}`;
      return sanitizeHtmlWithDomPurify
        ? DOMPurify.sanitize(content, purifyConfig)
        : content;
    }

    content = `${sectionTitle}${DocumentPresentationUtils.addSectionWrappingHtml(
      content,
    )}`;

    return sanitizeHtmlWithDomPurify
      ? DOMPurify.sanitize(content, purifyConfig)
      : content;
  }

  static disableCheckboxesInSectionContent = (section: string) => {
    const changeRequestWrapperDiv = document.createElement('div');
    changeRequestWrapperDiv.innerHTML = section;
    const checkboxes = changeRequestWrapperDiv.querySelectorAll(
      '.ck-content input[type=checkbox]',
    );
    checkboxes.forEach((checkbox) => {
      checkbox.classList.add('non-editable-mode-wrapper');
      // Normalize checked attribute: browser serializes checked="true" as bare "checked"
      // which CKEditor's checkbox plugin doesn't recognize. Convert back to checked="true"
      if (checkbox.hasAttribute('checked')) {
        checkbox.setAttribute('checked', 'true');
      }
    });

    return changeRequestWrapperDiv.innerHTML;
  };

  static determineHtmlContentFromSections(
    sections:
      | QualioDocumentSection[]
      | DocumentSectionRevision[]
      | ChangeControlSection[]
      | ChangeRequestSectionType[],
    includeCollabData = true,
    forExport = true,
    useLegacyExport = false,
    showSectionTitles = true,
    sanitizeHtmlWithDomPurify = false,
    disableCheckBoxes = false,
    tableOfContents: string | null = null,
  ): string {
    const purifyConfig = buildDOMPurifyConfig();
    let totalContent = '';
    if (tableOfContents !== null) {
      totalContent += styliseTOC(tableOfContents);
    }
    totalContent += sections
      .map((section) => {
        const sectionTitle = DocumentPresentationUtils.determineSectionTitleTag(
          section,
          showSectionTitles,
        );

        let content = includeCollabData
          ? section.content
          : DocumentPresentationUtils.stripCollabData(section.content);

        content = DocumentPresentationUtils.addFigureToTables(content);

        if (forExport) {
          content = DocumentPresentationUtils.convertEmbedsToLinks(
            content,
            true,
          );
        }

        if (!sanitizeHtmlWithDomPurify) {
          if (!showSectionTitles && forExport) {
            content = useLegacyExport ? content : `${sectionTitle}${content}`;
            return content;
          }
        } else {
          if (!showSectionTitles && forExport) {
            content = useLegacyExport ? content : `${sectionTitle}${content}`;
            return DOMPurify.sanitize(content, purifyConfig);
          }
        }

        content = `${sectionTitle}${DocumentPresentationUtils.addSectionWrappingHtml(
          content,
        )}`;

        if (disableCheckBoxes) {
          content =
            DocumentPresentationUtils.disableCheckboxesInSectionContent(
              content,
            );
        }

        if (sanitizeHtmlWithDomPurify) {
          return DOMPurify.sanitize(content, purifyConfig);
        }

        return content;
      })
      .join('');
    return totalContent;
  }

  static async convertEmbedsToContent(contentStr: string) {
    const container = document.createElement('div');
    container.innerHTML = contentStr;
    const figures = container.querySelectorAll('figure.media');

    if (!process.env.REACT_APP_QRI_SERVICE_URL) {
      return contentStr;
    }

    for (let index = 0; index < figures.length; index++) {
      const figure = figures[index];
      const oembed = figure.querySelector('div[data-oembed-url]');
      const embedUrl = oembed?.getAttribute('data-oembed-url');
      if (embedUrl && isQriUrl(embedUrl)) {
        const urlObj = new URL(embedUrl);
        const searchParams = new URLSearchParams(urlObj.search);
        const dataUrl = searchParams.get('dataUrl') || '';
        const companyId = searchParams.get('companyId') || '';
        const documentId = dataUrl.split('/').pop();
        if (companyId && documentId) {
          const fetchedDocument = await documentApi.fetchDocumentExportPayload(
            documentId,
            Number(companyId),
          );
          const exportPayload = fetchedDocument.exports[0];
          const documentHtml = DocumentPresentationUtils.convertEmbedsToLinks(
            exportPayload.html,
            true,
          );
          let transformedHtml =
            DocumentPresentationUtils.removeTableOfContentsFromHtml(
              documentHtml,
            );
          transformedHtml =
            DocumentPresentationUtils.applyCounterTransformations(
              index,
              transformedHtml,
            );
          const container = document.createElement('div');
          container.classList.add('document');
          container.classList.add('ck-content');
          container.innerHTML = transformedHtml;
          const parent = figure.parentElement;
          parent?.replaceChild(container, figure);
        }
      }
    }
    return container.innerHTML;
  }

  static convertEmbedsToLinks(
    contentStr: string,
    convertEmbeddedDocuments: boolean,
  ): string {
    const container = document.createElement('div');
    container.innerHTML = contentStr;
    const figures = container.querySelectorAll('figure.media');

    figures.forEach((figure) => {
      const oembed = figure.querySelector('oembed');
      const iframe = figure.querySelector('div[data-oembed-url]');
      const embed = oembed || iframe;
      const embedAttr = oembed ? 'url' : 'data-oembed-url';
      const embedUrl = embed?.getAttribute(embedAttr);
      if (embedUrl && (!isQriUrl(embedUrl) || convertEmbeddedDocuments)) {
        const link = document.createElement('a');
        const linkContainer = document.createElement('p');
        link.setAttribute('href', embedUrl);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        link.innerHTML = embedUrl;
        linkContainer.appendChild(link);
        const parent = figure.parentElement;
        parent?.replaceChild(linkContainer, figure);
      }
    });

    return container.innerHTML;
  }

  static addSectionWrappingHtml(editorContent: string): string {
    const contentToWrap =
      editorContent !== ''
        ? editorContent
        : '<p><br data-cke-filler="true"></p>';
    return (
      `<div data-cy="sectionContent" class="ck-restricted-editing_mode_restricted ck ck-content ck-editor__editable ck-rounded-corners ck-editor__editable_inline ck-blurred" >` +
      contentToWrap +
      '</div>'
    );
  }

  static determineSectionTitleTag(
    sectionMetadata: SectionMetadata,
    showTitle = true,
  ): string {
    let classes = 'section-heading';
    if (!showTitle) {
      classes += ' section-heading__hide';
    }

    return `<h1 class="${classes}" id="${slugify(
      sectionMetadata.title.toLowerCase(),
    )}"><span class="locked-section-headers">${
      sectionMetadata.position
    }. ${DocumentPresentationUtils.processTitleForView(
      sectionMetadata.title,
    )}</span></h1>`;
  }

  static processTitleForView(title: string): string {
    // replace multiple spaces with a single space
    const titleWithNoDoubleSpaces = title.replace(/\s\s+/g, ' ');
    return StringEscapeUtils.specialEscapeForCkEditor(
      titleWithNoDoubleSpaces,
    ).trim();
  }

  static getDocumentSectionsTitles(
    sections: QualioDocumentSection[] | ChangeControlSection[],
  ) {
    return sections.map(({ title }, index) => {
      return `${index + 1}. ${title}`;
    });
  }

  static stripCollabData(sectionContent: string): string {
    const parsedContent = parseHTMLString(sectionContent);
    const result = DocumentPresentationUtils.removeCollabAttributesFromContent(
      DocumentPresentationUtils.removeCollabTagsFromContent(parsedContent),
    );

    return result.body.innerHTML;
  }

  // documents converted from Froala but not edited
  // don't get its table elements wrapped in a <figure>
  // tag and therefore css is not correctly applied
  static addFigureToTables(sectionContent: string): string {
    const parsedContent = parseHTMLString(sectionContent);
    const sectionTables = parsedContent.querySelectorAll('table');
    Array.from(sectionTables).forEach((table) => {
      const parent = table.parentElement;

      if (parent === null) {
        return;
      }

      // Need to remove ckeditor's styling from the figure when table properties
      // are modified - https://github.com/ckeditor/ckeditor5/issues/15486
      if (parent.tagName === 'FIGURE') {
        const styleAttribute = parent.getAttribute('style');
        if (styleAttribute) {
          parent.style.height = '';
        }
        return;
      }

      const figure = document.createElement('figure');
      figure.className = 'table';
      const clonedTable = table.cloneNode(true);
      figure.appendChild(clonedTable);
      parent?.insertBefore(figure, table);
      table.remove();
    });

    return parsedContent.body.innerHTML;
  }

  static endOfLineInsertion(el: Element, closingTag: string): Element | null {
    let parent = el.parentElement;
    let hasClosingTag = parent?.querySelector(closingTag) !== null;
    while (parent && parent.nextSibling === null && !hasClosingTag) {
      parent = parent.parentElement;
      hasClosingTag = parent?.querySelector(closingTag) !== null;
    }
    return parent?.nextElementSibling ?? null;
  }

  static removeCollabSuggestedInsertion(
    suggestionStart: Element[],
    closingTag: string,
  ): void {
    suggestionStart.forEach((el) => {
      const name = el.getAttribute('name');
      if (name?.includes('insertion')) {
        let next: Element | null =
          (el.nextSibling as Element) ||
          DocumentPresentationUtils.endOfLineInsertion(el, closingTag);
        while (
          next &&
          (!next.tagName ||
            (next.tagName && next.tagName.toLocaleLowerCase() !== closingTag))
        ) {
          const sibling = next.nextSibling as Element;
          const hasClosingTag: boolean =
            next.nodeType === 3
              ? false
              : next.querySelector(closingTag) !== null;
          next.remove();
          next = hasClosingTag ? null : sibling;
        }
      }
    });
  }

  static replaceTDCollabTagsFromContent(
    suggestedTDs: Element[],
    content: Document,
  ): void {
    const startSuggestionAttr = 'data-suggestion-start-before';
    const endBeforeSuggestionAttr = 'data-suggestion-end-before';
    suggestedTDs.forEach((el) => {
      const suggestionAttr =
        el.getAttribute(startSuggestionAttr) ??
        el.getAttribute(endBeforeSuggestionAttr);
      if (suggestionAttr?.includes('deletion')) {
        el.parentElement?.insertAdjacentHTML('afterbegin', el.innerHTML);
      }
    });
  }

  static removeCollabTagsFromContent(content: Document): Document {
    const commentStartTag = 'comment-start';
    const commentEndTag = 'comment-end';
    const suggestionStartTag = 'suggestion-start';
    const suggestionEndTag = 'suggestion-end';
    const commentStart = Array.from(
      content.getElementsByTagName(commentStartTag),
    );
    const commentEnd = Array.from(content.getElementsByTagName(commentEndTag));
    const suggestionStart = Array.from(
      content.getElementsByTagName(suggestionStartTag),
    );
    const suggestionEnd = Array.from(
      content.getElementsByTagName(suggestionEndTag),
    );
    const startSuggestionInline = Array.from(
      content.querySelectorAll('[data-suggestion-start-before^=insertion]'),
    );
    const startAfterSuggestionInline = Array.from(
      content.querySelectorAll('[data-suggestion-start-after^=insertion]'),
    );
    const suggestedTDs = Array.from(
      content.getElementsByTagName('suggestion-td'),
    );
    DocumentPresentationUtils.replaceTDCollabTagsFromContent(
      suggestedTDs,
      content,
    );
    DocumentPresentationUtils.removeCollabSuggestedInsertion(
      suggestionStart,
      suggestionEndTag,
    );

    const toRemove = commentStart.concat(
      commentEnd,
      suggestionStart,
      suggestionEnd,
      startSuggestionInline,
      startAfterSuggestionInline,
      suggestedTDs,
    );
    toRemove.forEach((commentTag) => {
      commentTag.remove && commentTag.remove();
    });
    return content;
  }

  static removeCollabAttributesFromContent(content: Document): Document {
    const startCommentAttr = 'data-comment-start-before';
    const endCommentAttr = 'data-comment-end-after';
    const startAfterCommentAttr = 'data-comment-start-after';
    const endBeforeCommentAttr = 'data-comment-end-before';
    const startSuggestionAttr = 'data-suggestion-start-before';
    const endSuggestionAttr = 'data-suggestion-end-after';
    const startAfterSuggestionAttr = 'data-suggestion-start-after';
    const endBeforeSuggestionAttr = 'data-suggestion-end-before';

    const elementsWithCollabData = Array.from(
      content.querySelectorAll(`[${startCommentAttr}]`),
    ).concat(
      Array.from(content.querySelectorAll(`[${startSuggestionAttr}]`)),
      Array.from(content.querySelectorAll(`[${startAfterCommentAttr}]`)),
      Array.from(content.querySelectorAll(`[${startAfterSuggestionAttr}]`)),
    );
    elementsWithCollabData.forEach((el) => {
      el.removeAttribute(startCommentAttr);
      el.removeAttribute(endCommentAttr);
      el.removeAttribute(startAfterCommentAttr);
      el.removeAttribute(endBeforeCommentAttr);
      el.removeAttribute(startSuggestionAttr);
      el.removeAttribute(endSuggestionAttr);
      el.removeAttribute(startAfterSuggestionAttr);
      el.removeAttribute(endBeforeSuggestionAttr);
    });
    return content;
  }

  /**
   * PS-3599: https://qualio.atlassian.net/browse/PS-3599
   * Strip out any content that looks like: <mailto:testuser1@test.com>
   * because DOMParser will attempt to create it as an element in the html viewer
   */
  static readonly stripMailtoContent = (sectionContent: string) => {
    let processedContent = sectionContent;
    const mailtoMatches = Array.from(
      sectionContent.matchAll(new RegExp(MAIL_TO_REGEX, 'g')),
    );
    mailtoMatches.forEach((match) => {
      const [invalidContent, value] = match;
      const anchorLink = `<a href="mailto:${value}">${value}</a>`;
      // First replace invalid content with nothing
      processedContent = processedContent.replaceAll(invalidContent, '');
      // Then add anchor link to replace value with anchor link
      processedContent = processedContent.replaceAll(value, anchorLink);
    });
    return processedContent;
  };

  /**
   * PS-3860: https://qualio.atlassian.net/browse/PS-3860
   * Strip out any content that looks like: <tel:123...>
   * because DOMParser will attempt to create it as an element in the html viewer
   */
  static readonly stripTelContent = (sectionContent: string) => {
    let processedContent = sectionContent;
    const telMatches = Array.from(
      sectionContent.matchAll(new RegExp(TEL_REGEX, 'g')),
    );
    telMatches.forEach((match) => {
      const [invalidContent, value] = match;
      const anchorLink = `<a href="tel:${value}">${value}</a>`;
      // First replace invalid content with nothing
      processedContent = processedContent.replaceAll(invalidContent, '');
      // Then add anchor link to replace value with anchor link
      processedContent = processedContent.replaceAll(value, anchorLink);
    });
    return processedContent;
  };

  /**
   * PS-3860: https://qualio.atlassian.net/browse/PS-3860
   */
  static readonly stripEmailContent = (sectionContent: string) => {
    let processedContent = sectionContent;
    const emailMatches = Array.from(
      sectionContent.matchAll(new RegExp(EMAIL_REGEX, 'g')),
    );
    emailMatches.forEach((match) => {
      const anchorLink = `<a href="mailto:${match[0]}">${match[0]}</a>`;
      processedContent = processedContent.replaceAll(
        `<${match[0]}>`,
        anchorLink,
      );
    });
    processedContent = processedContent.replace(/&lt;/g, '');
    processedContent = processedContent.replace(/&gt;/g, '');
    return processedContent;
  };

  static removeTableOfContentsFromHtml(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html;
    const toc = container.querySelector('.table-of-contents');
    toc?.remove();
    return container.innerHTML;
  }

  static applyCounterTransformations(index: number, html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html;
    container.querySelectorAll('.section-heading').forEach((el) => {
      el.classList.replace(
        'section-heading',
        `section-heading-frame-${index + 1}`,
      );
    });
    container.innerHTML = container.innerHTML.replaceAll(
      'multilevel',
      `multilevelFrame${index + 1}`,
    );
    return container.innerHTML;
  }
}
